import { createContext, useContext, useEffect, useId, useRef, useState, useSyncExternalStore, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { Button, IconButton } from '../Button/Button';
import { FormField } from '../Field/Field';
import { Icon } from '../Icon/Icon';
import { UI_DEFAULTS } from '@gr/core/platform/config/ui-config';
import { AsyncOptions, type AsyncOptionsState, type ComboboxOption, type LoadOptions } from './async-options';
import styles from './Combobox.module.scss';

export type { ComboboxOption, LoadOptions, OptionsPage } from './async-options';

/** Тексты списка. ui-kit не знает словарей — их даёт приложение (UiProviders). */
export interface ComboboxTexts {
  loading: string;
  empty: string;
  failed: string;
  retry: string;
  clear: string;
  remove: string;
}

const ComboboxTextsContext = createContext<ComboboxTexts>({
  loading: 'Loading…',
  empty: 'Nothing found',
  failed: 'Failed to load',
  retry: 'Retry',
  clear: 'Clear',
  remove: 'Remove',
});

export function ComboboxTextsProvider({ texts, children }: { texts: ComboboxTexts; children: ReactNode }): ReactElement {
  return <ComboboxTextsContext.Provider value={texts}>{children}</ComboboxTextsContext.Provider>;
}

/** Поведение списка: пауза перед поиском и порог подгрузки. Значения — из конфигурации (UiProviders). */
export interface ComboboxBehavior {
  searchDelayMs: number;
  loadMoreEdgePx: number;
}

const ComboboxBehaviorContext = createContext<ComboboxBehavior>({
  searchDelayMs: UI_DEFAULTS.search.delayMs,
  loadMoreEdgePx: UI_DEFAULTS.combobox.loadMoreEdgePx,
});

export function ComboboxBehaviorProvider({ behavior, children }: { behavior: ComboboxBehavior; children: ReactNode }): ReactElement {
  return <ComboboxBehaviorContext.Provider value={behavior}>{children}</ComboboxBehaviorContext.Provider>;
}

/** Варианты для одного поля: загрузчик живёт, пока живёт поле. searchDelayMs — своя пауза поля. */
export function useAsyncOptions(load: LoadOptions, searchDelayMs?: number): { state: AsyncOptionsState; options: AsyncOptions } {
  const behavior = useContext(ComboboxBehaviorContext);
  const ref = useRef<AsyncOptions | null>(null);
  ref.current ??= new AsyncOptions(load, undefined, searchDelayMs ?? behavior.searchDelayMs);
  const options = ref.current;
  options.setLoader(load);
  const state = useSyncExternalStore(options.subscribe, options.getState, options.getState);
  useEffect(() => () => options.close(), [options]);
  return { state, options };
}

interface ComboboxBase {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  load: LoadOptions;
  /** Подписи выбранных значений: их может не быть среди загруженных вариантов (ENT-UC-8.CC-2). */
  selectedLabels?: Readonly<Record<string, string>>;
  /** Пауза перед поиском; для списка, который уже в памяти (часовые пояса), — 0. */
  searchDelayMs?: number;
}

export interface SingleComboboxProps extends ComboboxBase {
  multiple?: false;
  value: string | null;
  onChange(value: string | null): void;
}

export interface MultiComboboxProps extends ComboboxBase {
  multiple: true;
  value: readonly string[];
  onChange(value: string[]): void;
}

export type ComboboxProps = SingleComboboxProps | MultiComboboxProps;

/** Отступ до конца списка, с которого подгружается следующая страница. */

/**
 * Автокомплит (UX-стереотип «Combobox», ENT-UC-8): ввод прямо в поле, варианты с сервера
 * страницами, поиск после паузы. Разметка и клавиатура — паттерн WAI-ARIA combobox. Один
 * вариант — текст поля, несколько — чипы перед вводом.
 */
export function Combobox(props: ComboboxProps): ReactElement {
  const { label, hint, error, required, disabled, placeholder, load, selectedLabels, searchDelayMs } = props;
  const texts = useContext(ComboboxTextsContext);
  const { loadMoreEdgePx } = useContext(ComboboxBehaviorContext);
  const listId = useId();
  const { state, options } = useAsyncOptions(load, searchDelayMs);
  const [open, setOpen] = useState(false);
  // null — пользователь ничего не вводил: в поле одиночного выбора виден выбранный вариант.
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const [chosen, setChosen] = useState<Readonly<Record<string, string>>>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected: readonly string[] = props.multiple ? props.value : props.value ? [props.value] : [];
  const labelOf = (value: string): string => selectedLabels?.[value] ?? chosen[value] ?? state.items.find((o) => o.value === value)?.label ?? value;
  const text = query ?? (!props.multiple && props.value ? labelOf(props.value) : '');

  useEffect(() => {
    if (active >= 0) listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const openList = (): void => {
    if (disabled || open) return;
    setOpen(true);
    setActive(-1);
    options.open('');
    // На телефоне поле поднимается, чтобы список не закрыла клавиатура.
    if (window.matchMedia('(pointer: coarse)').matches) rootRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  const closeList = (): void => {
    setOpen(false);
    setQuery(null);
    setActive(-1);
    options.close();
  };

  const type = (next: string): void => {
    setQuery(next);
    setActive(-1);
    setOpen(true);
    options.search(next);
  };

  const choose = (option: ComboboxOption): void => {
    setChosen((known) => ({ ...known, [option.value]: option.label }));
    if (props.multiple) {
      props.onChange(selected.includes(option.value) ? selected.filter((v) => v !== option.value) : [...selected, option.value]);
      return;
    }
    props.onChange(option.value);
    closeList();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        if (!open) return openList();
        const next = Math.min(active + 1, state.items.length - 1);
        if (next >= state.items.length - 1) options.loadMore();
        setActive(next);
        return;
      }
      case 'ArrowUp':
        event.preventDefault();
        if (open) setActive(Math.max(active - 1, 0));
        return;
      case 'Enter': {
        const option = open && active >= 0 ? state.items[active] : undefined;
        if (!option) return;
        event.preventDefault();
        choose(option);
        return;
      }
      case 'Escape':
        if (!open) return;
        // Не даём Esc закрыть диалог, в котором стоит поле: закрываем только список.
        event.preventDefault();
        event.stopPropagation();
        closeList();
        return;
      case 'Backspace':
        if (props.multiple && !query && selected.length > 0) props.onChange(selected.slice(0, -1));
        return;
    }
  };

  const clearable = !props.multiple && !required && !disabled && props.value !== null;

  return (
    <FormField label={label} hint={hint} error={error} required={required}>
      {({ id, describedBy, invalid }) => (
        <div ref={rootRef} className={styles.root}>
          <div
            className={`${styles.control} ${invalid ? styles.invalid : ''} ${disabled ? styles.disabled : ''}`}
            onMouseDown={(event) => {
              if (event.target === inputRef.current || (event.target as HTMLElement).closest('button')) return;
              event.preventDefault();
              inputRef.current?.focus();
              openList();
            }}
          >
            {props.multiple
              ? selected.map((value) => (
                  <span key={value} className={styles.chip} title={labelOf(value)}>
                    <span className={styles.chipLabel}>{labelOf(value)}</span>
                    <IconButton
                      icon="close"
                      size="sm"
                      label={`${texts.remove}: ${labelOf(value)}`}
                      className={styles.chipRemove}
                      disabled={disabled}
                      onClick={() => props.onChange(selected.filter((v) => v !== value))}
                    />
                  </span>
                ))
              : null}
            <input
              ref={inputRef}
              id={id}
              className={styles.input}
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              aria-required={required}
              autoComplete="off"
              placeholder={selected.length > 0 && props.multiple ? undefined : placeholder}
              disabled={disabled}
              value={text}
              onFocus={openList}
              onClick={openList}
              onBlur={closeList}
              onChange={(event) => type(event.target.value)}
              onKeyDown={onKeyDown}
            />
            {clearable ? <IconButton icon="close" size="sm" label={texts.clear} className={styles.clear} onClick={() => props.onChange(null)} /> : null}
            <span className={styles.chevron} aria-hidden="true">
              <Icon name="down" size={16} />
            </span>
          </div>
          {open ? (
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              aria-label={label}
              aria-multiselectable={props.multiple || undefined}
              aria-busy={state.loading}
              className={styles.list}
              // Клик по списку не уводит фокус из поля — иначе список закроется раньше выбора.
              onMouseDown={(event) => event.preventDefault()}
              onScroll={(event) => {
                const list = event.currentTarget;
                if (list.scrollTop + list.clientHeight >= list.scrollHeight - loadMoreEdgePx) options.loadMore();
              }}
            >
              {state.items.map((option, index) => {
                const isSelected = selected.includes(option.value);
                return (
                  <li
                    key={option.value}
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    className={`${styles.option} ${index === active ? styles.active : ''} ${isSelected ? styles.selected : ''}`}
                    onClick={() => choose(option)}
                    onMouseEnter={() => setActive(index)}
                  >
                    <span className={styles.optionLabel}>{option.label}</span>
                    {isSelected ? <Icon name="check" size={16} /> : null}
                  </li>
                );
              })}
              <ListStatus state={state} texts={texts} onRetry={() => options.retry()} />
            </ul>
          ) : null}
        </div>
      )}
    </FormField>
  );
}

/** Строка состояния под вариантами: загрузка, пусто или ошибка с повтором. */
function ListStatus({ state, texts, onRetry }: { state: AsyncOptionsState; texts: ComboboxTexts; onRetry(): void }): ReactElement | null {
  if (state.failed) {
    return (
      <li className={styles.status} role="presentation">
        <span role="alert">{texts.failed}</span>
        <Button size="sm" variant="ghost" onClick={onRetry}>
          {texts.retry}
        </Button>
      </li>
    );
  }
  if (state.loading || !state.loaded) {
    return (
      <li className={styles.status} role="presentation" aria-live="polite">
        <span className={styles.spinner} aria-hidden="true" />
        {texts.loading}
      </li>
    );
  }
  if (state.items.length === 0) {
    return (
      <li className={styles.status} role="presentation">
        {texts.empty}
      </li>
    );
  }
  return null;
}
